/**
 * Routing Attempts Database Hook
 * 
 * Provides database operations for tracking routing attempts per lead.
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import type { 
  RoutingAttempt, 
  RoutingAttemptSource, 
  RoutingAttemptResult,
  RoutingAttemptStats,
  GuardrailsApplied,
  AttemptScoring,
  DevOverridesContext,
  RetryContext,
} from '@/lib/leads/routingAttemptTypes';

// --- Database Row Type ---

interface RoutingAttemptRow {
  id: string;
  lead_id: string;
  attempt_number: number;
  source: string;
  result: string;
  agent_id: string | null;
  agent_name: string | null;
  charged_amount: number | null;
  priority_score: number | null;
  bid_score: number | null;
  quality_score: number | null;
  final_score: number | null;
  eligible_agents_count: number;
  ineligible_agents_count: number;
  total_agents_evaluated: number;
  guardrails_applied: boolean;
  pacing_cap_applied: boolean;
  pool_boost_applied: boolean;
  retry_priority_applied: boolean;
  dev_overrides_applied: boolean;
  rejection_code: string | null;
  rejection_reason: string | null;
  routing_event_id: string | null;
  simulation_run_id: string | null;
  started_at: number;
  completed_at: number;
  duration_ms: number;
  created_at: string;
}

// --- Transform Functions ---

function rowToAttempt(row: RoutingAttemptRow): RoutingAttempt {
  return {
    id: row.id,
    leadId: row.lead_id,
    attemptNumber: row.attempt_number,
    source: row.source as RoutingAttemptSource,
    result: row.result as RoutingAttemptResult,
    agentId: row.agent_id ?? undefined,
    agentName: row.agent_name ?? undefined,
    chargedAmount: row.charged_amount ?? undefined,
    scoring: {
      priorityScore: row.priority_score ?? undefined,
      bidScore: row.bid_score ?? undefined,
      qualityScore: row.quality_score ?? undefined,
      finalScore: row.final_score ?? undefined,
    },
    eligibleAgentsCount: row.eligible_agents_count,
    ineligibleAgentsCount: row.ineligible_agents_count,
    totalAgentsEvaluated: row.total_agents_evaluated,
    guardrailsApplied: row.guardrails_applied,
    guardrails: {
      pacingCap: row.pacing_cap_applied,
      poolBoost: row.pool_boost_applied,
      retryPriority: row.retry_priority_applied,
    },
    devOverridesApplied: row.dev_overrides_applied,
    // Note: devOverridesContext is stored in routing_context on the lead, not in routing_attempts
    // This is fine for now since the detailed attribution is logged to console
    rejectionCode: row.rejection_code ?? undefined,
    rejectionReason: row.rejection_reason ?? undefined,
    routingEventId: row.routing_event_id ?? undefined,
    simulationRunId: row.simulation_run_id ?? undefined,
    startedAt: row.started_at,
    completedAt: row.completed_at,
    durationMs: row.duration_ms,
    createdAt: row.created_at,
  };
}

// --- Query Keys ---

export const routingAttemptsQueryKeys = {
  all: ['routing-attempts'] as const,
  byLeadId: (leadId: string) => ['routing-attempts', 'lead', leadId] as const,
  bySimulationRun: (runId: string) => ['routing-attempts', 'simulation', runId] as const,
  recent: (limit: number) => ['routing-attempts', 'recent', limit] as const,
  stats: () => ['routing-attempts', 'stats'] as const,
};

// --- Hooks ---

export function useRoutingAttempts(limit: number = 100) {
  return useQuery({
    queryKey: routingAttemptsQueryKeys.recent(limit),
    queryFn: async () => {
      const { data, error } = await supabase
        .from('routing_attempts')
        .select('*')
        .order('created_at', { ascending: false })
        .limit(limit);
      
      if (error) throw error;
      return (data as unknown as RoutingAttemptRow[]).map(rowToAttempt);
    },
  });
}

export function useRoutingAttemptsByLeadId(leadId: string) {
  return useQuery({
    queryKey: routingAttemptsQueryKeys.byLeadId(leadId),
    queryFn: async () => {
      const { data, error } = await supabase
        .from('routing_attempts')
        .select('*')
        .eq('lead_id', leadId)
        .order('attempt_number', { ascending: true });
      
      if (error) throw error;
      return (data as unknown as RoutingAttemptRow[]).map(rowToAttempt);
    },
    enabled: !!leadId,
  });
}

export function useRoutingAttemptsBySimulationRun(simulationRunId: string) {
  return useQuery({
    queryKey: routingAttemptsQueryKeys.bySimulationRun(simulationRunId),
    queryFn: async () => {
      const { data, error } = await supabase
        .from('routing_attempts')
        .select('*')
        .eq('simulation_run_id', simulationRunId)
        .order('created_at', { ascending: false });
      
      if (error) throw error;
      return (data as unknown as RoutingAttemptRow[]).map(rowToAttempt);
    },
    enabled: !!simulationRunId,
  });
}

export function useRoutingAttemptStats() {
  return useQuery({
    queryKey: routingAttemptsQueryKeys.stats(),
    queryFn: async () => {
      const { data, error } = await supabase
        .from('routing_attempts')
        .select('result, source, duration_ms')
        .is('simulation_run_id', null); // Only count live attempts
      
      if (error) throw error;
      
      const byResult: Record<RoutingAttemptResult, number> = {
        ROUTED: 0,
        UNSOLD: 0,
        RETRY_QUEUED: 0,
        STOCKED: 0,
        VALIDATION_FAILED: 0,
        CREDITED: 0,
      };
      
      const bySource: Record<RoutingAttemptSource, number> = {
        LIVE: 0,
        RETRY: 0,
        SIMULATION: 0,
      };
      
      let totalDuration = 0;
      
      for (const row of data as { result: string; source: string; duration_ms: number }[]) {
        byResult[row.result as RoutingAttemptResult]++;
        bySource[row.source as RoutingAttemptSource]++;
        totalDuration += row.duration_ms;
      }
      
      const total = data.length;
      const successRate = total > 0 ? byResult.ROUTED / total : 0;
      const averageDurationMs = total > 0 ? totalDuration / total : 0;
      
      return {
        total,
        byResult,
        bySource,
        averageDurationMs,
        successRate,
      } as RoutingAttemptStats;
    },
  });
}

// --- Mutations ---

export interface CreateRoutingAttemptInput {
  id: string;
  leadId: string;
  attemptNumber: number;
  source: RoutingAttemptSource;
  result: RoutingAttemptResult;
  agentId?: string;
  agentName?: string;
  chargedAmount?: number;
  scoring?: AttemptScoring;
  eligibleAgentsCount: number;
  ineligibleAgentsCount: number;
  totalAgentsEvaluated: number;
  guardrailsApplied: boolean;
  guardrails?: GuardrailsApplied;
  devOverridesApplied?: boolean;
  /** Full DEV override context with attribution (logged but not persisted to DB) */
  devOverridesContext?: DevOverridesContext;
  /** Retry context with re-evaluation metadata (logged but not persisted to DB) */
  retryContext?: RetryContext;
  rejectionCode?: string;
  rejectionReason?: string;
  routingEventId?: string;
  simulationRunId?: string;
  startedAt: number;
  completedAt: number;
  durationMs: number;
}

export function useCreateRoutingAttempt() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async (attempt: CreateRoutingAttemptInput) => {
      const { data, error } = await supabase
        .from('routing_attempts')
        .insert({
          id: attempt.id,
          lead_id: attempt.leadId,
          attempt_number: attempt.attemptNumber,
          source: attempt.source,
          result: attempt.result,
          agent_id: attempt.agentId ?? null,
          agent_name: attempt.agentName ?? null,
          charged_amount: attempt.chargedAmount ?? null,
          priority_score: attempt.scoring?.priorityScore ?? null,
          bid_score: attempt.scoring?.bidScore ?? null,
          quality_score: attempt.scoring?.qualityScore ?? null,
          final_score: attempt.scoring?.finalScore ?? null,
          eligible_agents_count: attempt.eligibleAgentsCount,
          ineligible_agents_count: attempt.ineligibleAgentsCount,
          total_agents_evaluated: attempt.totalAgentsEvaluated,
          guardrails_applied: attempt.guardrailsApplied,
          pacing_cap_applied: attempt.guardrails?.pacingCap ?? false,
          pool_boost_applied: attempt.guardrails?.poolBoost ?? false,
          retry_priority_applied: attempt.guardrails?.retryPriority ?? false,
          dev_overrides_applied: attempt.devOverridesApplied ?? false,
          rejection_code: attempt.rejectionCode ?? null,
          rejection_reason: attempt.rejectionReason ?? null,
          routing_event_id: attempt.routingEventId ?? null,
          simulation_run_id: attempt.simulationRunId ?? null,
          started_at: attempt.startedAt,
          completed_at: attempt.completedAt,
          duration_ms: attempt.durationMs,
        })
        .select()
        .single();
      
      if (error) throw error;
      return rowToAttempt(data as unknown as RoutingAttemptRow);
    },
    onSuccess: (attempt) => {
      queryClient.invalidateQueries({ queryKey: routingAttemptsQueryKeys.all });
      queryClient.invalidateQueries({ queryKey: routingAttemptsQueryKeys.byLeadId(attempt.leadId) });
      queryClient.invalidateQueries({ queryKey: routingAttemptsQueryKeys.stats() });
    },
  });
}

export function useDeleteAttemptsBySimulationRun() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async (simulationRunId: string) => {
      const { error } = await supabase
        .from('routing_attempts')
        .delete()
        .eq('simulation_run_id', simulationRunId);
      
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: routingAttemptsQueryKeys.all });
      queryClient.invalidateQueries({ queryKey: routingAttemptsQueryKeys.stats() });
    },
  });
}

// --- Standalone function for use in non-React contexts ---

export async function recordRoutingAttemptToDb(
  attempt: CreateRoutingAttemptInput
): Promise<RoutingAttempt> {
  // Log retry context if present (for observability)
  if (attempt.retryContext?.isRetry) {
    console.log('[RoutingAttempts] RETRY attempt context:');
    console.log(`  Previous attempt: #${attempt.retryContext.previousAttemptNumber}`);
    console.log(`  Previous status: ${attempt.retryContext.previousStatus}`);
    console.log(`  DEV overrides re-evaluated: ${attempt.retryContext.devOverridesReEvaluated}`);
    console.log(`  Eligibility computed fresh: ${attempt.retryContext.eligibilityComputedFresh}`);
    console.log(`  Retry reason: ${attempt.retryContext.retryReason}`);
  }
  
  // Log DEV override attribution if present (for debugging)
  if (attempt.devOverridesContext?.applied && attempt.devOverridesContext.agentAttributions) {
    console.log('[RoutingAttempts] DEV override attribution:');
    for (const [agentId, attr] of Object.entries(attempt.devOverridesContext.agentAttributions)) {
      const bypassed = Object.entries(attr)
        .filter(([, v]) => v.bypassed)
        .map(([k, v]) => `${k}${v.reason ? ` (${v.reason})` : ''}`);
      const enforced = Object.entries(attr)
        .filter(([, v]) => !v.bypassed)
        .map(([k]) => k);
      console.log(`  Agent ${agentId}:`);
      console.log(`    Bypassed: [${bypassed.join(', ')}]`);
      console.log(`    Enforced: [${enforced.join(', ')}]`);
    }
  }
  
  const { data, error } = await supabase
    .from('routing_attempts')
    .insert({
      id: attempt.id,
      lead_id: attempt.leadId,
      attempt_number: attempt.attemptNumber,
      source: attempt.source,
      result: attempt.result,
      agent_id: attempt.agentId ?? null,
      agent_name: attempt.agentName ?? null,
      charged_amount: attempt.chargedAmount ?? null,
      priority_score: attempt.scoring?.priorityScore ?? null,
      bid_score: attempt.scoring?.bidScore ?? null,
      quality_score: attempt.scoring?.qualityScore ?? null,
      final_score: attempt.scoring?.finalScore ?? null,
      eligible_agents_count: attempt.eligibleAgentsCount,
      ineligible_agents_count: attempt.ineligibleAgentsCount,
      total_agents_evaluated: attempt.totalAgentsEvaluated,
      guardrails_applied: attempt.guardrailsApplied,
      pacing_cap_applied: attempt.guardrails?.pacingCap ?? false,
      pool_boost_applied: attempt.guardrails?.poolBoost ?? false,
      retry_priority_applied: attempt.guardrails?.retryPriority ?? false,
      dev_overrides_applied: attempt.devOverridesApplied ?? false,
      rejection_code: attempt.rejectionCode ?? null,
      rejection_reason: attempt.rejectionReason ?? null,
      routing_event_id: attempt.routingEventId ?? null,
      simulation_run_id: attempt.simulationRunId ?? null,
      started_at: attempt.startedAt,
      completed_at: attempt.completedAt,
      duration_ms: attempt.durationMs,
    })
    .select()
    .single();
  
  if (error) {
    console.error('[RoutingAttempts] Failed to record attempt:', error);
    throw error;
  }
  
  return rowToAttempt(data as unknown as RoutingAttemptRow);
}
